import { Loading, NudosTextInput } from '../../../../../../../../components/NudosComponents';
import useStockOrdersModuleState from '../../../../../../../../state/useStockOrdersModuleState';
import { IDetailProviders } from '../../../../../../../../types/products';
import { createNewProvider, getProviders } from 'services/orders';
import { useEffect, useState } from 'react';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import GenericSearchBar from 'components/genericSearchBar/GenericSearchBar';
import './ProviderInformation.scss';

/**
 * Componente que maneja la información del proveedor en la creación de órdenes de stock
 */
const ProviderInformation = () => {
  const {
    selectedProvider,
    setSelectedProvider,
    orderProvider,
    setOrderProvider,
    invoiceProvider,
    setInvoiceProvider,
    selectedCountryId,
    selectedCountryCode
  } = useStockOrdersModuleState();

  const [searchFilter, setSearchFilter] = useState('');
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [providersList, setProvidersList] = useState<IDetailProviders[]>([]);
  const [openProviderList, setOpenProviderList] = useState(false);

  const searchProviders = async () => {
    if (!searchFilter || searchFilter?.length === 0) return;
    setLoadingProviders(true);
    try {
      const providers = await getProviders(searchFilter.toLowerCase());
      if (providers) {
        const providersInThisCountry = providers.filter(provider => provider?.countryId === selectedCountryId);
        setProvidersList(providersInThisCountry as IDetailProviders[]);
        setLoadingProviders(false);
      }
    } catch (error) {
      displayErrorNotification({ customJSXMessage: <>Error al cargar los proveedores</> });
      setLoadingProviders(false);
    }
  };

  const handleChangeProvider = (newProvider: string, providerId: number, createdAt: string, updatedAt: string) => {
    setSelectedProvider({
      id: providerId,
      name: newProvider,
      createdAt: createdAt,
      updatedAt: updatedAt,
      deletedAt: null
    });
    setSearchFilter(newProvider);
    setOpenProviderList(false);
  };

  const createProvider = async () => {
    if (!selectedCountryId || !selectedCountryCode)
      return displayErrorNotification({ customJSXMessage: <>Error al crear el proveedor</> });
    const body = { countryId: selectedCountryId, countryCode: selectedCountryCode, name: searchFilter };
    setLoadingProviders(true);
    try {
      const response = await createNewProvider(body);
      if (response && response?.name && response?.id)
        handleChangeProvider(response.name, response.id, response.createdAt, response.updatedAt);

      setLoadingProviders(false);
    } catch (error) {
      displayErrorNotification({ customJSXMessage: <>Error al crear el proveedor</> });
      setLoadingProviders(false);
    }
  };

  const newProviderOption = (
    <div key={`${selectedProvider?.id}-newProviderOption`} className="newProviderOption" onClick={createProvider}>
      <div className="searchedText">{searchFilter} - </div>
      <div className="newText"> Nuevo</div>
    </div>
  );

  const formProvidersOptionsUI = () => {
    if (!providersList || providersList.length === 0) return undefined;
    const providersListJSXElements = providersList?.map((provider, i) => {
      return (
        <div
          key={`provider-${provider.id}-${i}`}
          className="option-container"
          onClick={() => handleChangeProvider(provider.name, provider.id, provider.createdAt, provider.updatedAt)}
        >
          <p>{provider.name}</p>
        </div>
      );
    });
    const providersListAndNewProviderOption = [...providersListJSXElements, newProviderOption];
    return providersListAndNewProviderOption;
  };

  useEffect(() => {
    if (searchFilter && searchFilter?.length > 0) searchProviders();
  }, [searchFilter]);

  return (
    <div className="providerInformation">
      <div className="text-nudos-text-color text-sm font-extrabold">Información del proveedor</div>
      <div className="mt-3 bg-nudos-gray-100 rounded-lg p-2 flex items-center gap-2">
        <div className="inputContainer">
          <div className="text-nudos-text-color text-xss">Proveedor*</div>
          <div className='inputProvider'>
            <GenericSearchBar
              searchString={searchFilter}
              setSearchString={setSearchFilter}
              setIsSelectOpen={setOpenProviderList}
              searchedOptions={formProvidersOptionsUI()}
              isSelectOpen={openProviderList}
              initialValue={selectedProvider?.name || undefined}
              handleChangeSelectedOption={(newProvider: string) => handleChangeProvider(newProvider, 0, '', '')}
              noResultsMessage={loadingProviders ? <Loading key="loading" /> : newProviderOption}
              customClassName="w-44"
              customPlaceholder="Busca por proveedor"
            />
          </div>
        </div>
        <NudosTextInput
          label="N° de orden de proveedor*"
          componentSize="medium"
          placeholder="Agregar n° de orden"
          defaultValue={orderProvider || ''}
          isFilled={!!orderProvider}
          handleChange={setOrderProvider}
        />
        <NudosTextInput
          label="N° de factura"
          componentSize="medium"
          placeholder="Agregar n° de factura"
          defaultValue={invoiceProvider || ''}
          isFilled={!!invoiceProvider}
          handleChange={setInvoiceProvider}
        />
      </div>
    </div>
  );
};

export default ProviderInformation;
